import React, { useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import type {
	cascadingSelect_issueFieldCascadingSelectReadviewFull_CascadingSelectReadView$key as CascadingSelectFragment,
	cascadingSelect_issueFieldCascadingSelectReadviewFull_CascadingSelectReadView$data as AggCascadingSelectTypes,
} from '@atlassian/jira-relay/src/__generated__/cascadingSelect_issueFieldCascadingSelectReadviewFull_CascadingSelectReadView.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { HoverPopoverWithContext } from '@atlassian/jira-hover-popover/src/ui/popover-with-context/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import type { CascadingSelectReadViewProps } from './types.tsx';
import { CascadingSelectOptionTruncated } from './truncated/index.tsx';

const getFieldValueAsText = (cascadingOption: AggCascadingSelectTypes['cascadingOption']) => {
	if (!cascadingOption || !cascadingOption?.parentOptionValue) {
		return null;
	}

	if (!cascadingOption?.childOptionValue) {
		return cascadingOption.parentOptionValue.value;
	}

	return `${cascadingOption.parentOptionValue.value} - ${cascadingOption.childOptionValue.value}`;
};

/**
 * The CascadingSelectReadView will show a read only view of CascadingSelect field.
 * @param props [CascadingSelectReadViewProps](./types.tsx)
 */
export const CascadingSelectReadView = ({
	fragmentRef,
	isTruncated = false,
	shouldRenderHoverPopover,
}: CascadingSelectReadViewProps) => {
	const data = useFragment<CascadingSelectFragment>(
		graphql`
			fragment cascadingSelect_issueFieldCascadingSelectReadviewFull_CascadingSelectReadView on JiraCascadingSelectField
			@argumentDefinitions(
				isSingleLineEventDelegatorEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-single-line-event-delegator-enabled.relayprovider"
				}
			) {
				id @include(if: $isSingleLineEventDelegatorEnabled)
				cascadingOption {
					parentOptionValue {
						value
					}
					childOptionValue {
						value
					}
				}
			}
		`,
		fragmentRef,
	);

	const { cascadingOption } = data;

	const fieldValueAsText = getFieldValueAsText(cascadingOption);
	const contentRef = useRef(null);

	const hoverPopoverRefFactory =
		shouldRenderHoverPopover && fg('issue_table_single_line_row_height_event_delegator')
			? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
				useHoverPopoverContentRef(data.id)
			: null;

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: fieldValueAsText,
		});
	}

	if (!fieldValueAsText) {
		return <EmptyFieldText />;
	}

	if (isTruncated) {
		if (shouldRenderHoverPopover) {
			return fg('issue_table_single_line_row_height_event_delegator') ? (
				<CascadingSelectOptionTruncated value={fieldValueAsText} ref={hoverPopoverRef} />
			) : (
				<HoverPopoverWithContext content={fieldValueAsText} contentRef={contentRef}>
					{({ ref }) => (
						<CascadingSelectOptionTruncated
							value={fieldValueAsText}
							ref={mergeRefs(ref, contentRef)}
						/>
					)}
				</HoverPopoverWithContext>
			);
		}
		return <CascadingSelectOptionTruncated value={fieldValueAsText} />;
	}
	return <>{fieldValueAsText}</>;
};
